import React, { useState, useMemo } from 'react';

type LogAction = '등록' | '수정' | '다운로드' | '이메일 전송' | '삭제';

interface SimpleLogEntry {
    id: string;
    timestamp: string;
    userName: string;
    userEmail: string;
    menuPath: string;
    action: LogAction;
    message: string;
    ipAddress: string;
    details: any;
}

const generateMockLogs = (): SimpleLogEntry[] => {
    const logs: SimpleLogEntry[] = [];
    const sensibleLogs: { path: string, action: LogAction, messages: string[] }[] = [
        { path: '관리 > 법인 차량', action: '등록', messages: ['신규 법인 차량(45가9921) 제원 정보 등록', '업무용 승용차(12하3456) 시스템 신규 편입'] },
        { path: '관리 > 법인 차량', action: '수정', messages: ['차량(11가1234)의 보험 갱신 정보 업데이트', '차량 상세 제원 및 옵션 사항 수정'] },
        { path: '관리 > 법인 차량', action: '다운로드', messages: ['전체 법인 차량 보유 현황 리스트 (CSV) 추출'] },
        { path: '관리 > 운전자', action: '수정', messages: ['운전자(김철수) 상세 프로필 및 연락처 변경', '운전면허증 사본 이미지 교체 업로드'] },
        { path: '관리 > 운전자', action: '삭제', messages: ['퇴사자(이동욱) 운전자 계정 시스템 영구 삭제', '중복 등록된 예비 운전자 프로필 3건 삭제'] },
        { path: '관제 > 배터리 관제', action: '다운로드', messages: ['월간 차량별 실시간 배터리 상태 진단 결과 (Excel) 내보내기'] },
        { path: '운영 > 운행 기록 관리', action: '다운로드', messages: ['강남지점 2월 운행 기록부 (PDF) 다운로드 완료', '국세청 제출용 분기별 운행일지 엑셀 추출'] },
        { path: '운영 > 운행 기록 관리', action: '이메일 전송', messages: ['부서장(jeong@eview.app)에게 주간 운행 요약본 메일 발송', '미작성 운행 기록 알림 이메일 12명 일괄 전송'] },
        { path: '운영 > 운행 기록 관리', action: '삭제', messages: ['과거 3년 전 불필요한 운행 기록 150건 강제 삭제'] },
        { path: '운영 > 사고 및 정비 이력', action: '등록', messages: ['차량(55다7788) 경미한 접촉 사고 이력 신규 등록', '수리 견적서 및 결제 영수증 첨부파일 추가'] },
        { path: '운영 > 사고 및 정비 이력', action: '수정', messages: ['진행 중이던 수리 내역을 완료 상태로 변경', '사고 과실 비율 및 처리부서 정보 수정'] },
        { path: '관리자 설정 > 사용자 계정 관리', action: '수정', messages: ['신규 가입자 관리자 승인 (운영 실무자 권한 부여)', '특정 관리자(user7)를 접근 제한 상태로 강등'] },
        { path: '관리자 설정 > 사용자 계정 관리', action: '삭제', messages: ['비활성 계정 5건 일괄 삭제 처리'] },
        { path: '관리자 설정 > 권한 그룹 설정', action: '수정', messages: ['운영 실무자 권한 그룹의 삭제 스위치 비활성화', '모니터링 담당 그룹의 다운로드 권한 추가'] },
    ];
    const users = [
        { name: '정지훈', email: 'master@eview.app', ip: '211.34.55.12' },
        { name: '김태희', email: 'operator1@eview.app', ip: '192.168.0.5' },
        { name: '이동욱', email: 'linkage@eview.app', ip: '110.22.33.44' },
    ];

    let now = new Date();
    for (let i = 1; i <= 200; i++) {
        const user = users[Math.floor(Math.random() * users.length)];
        const pool = sensibleLogs[Math.floor(Math.random() * sensibleLogs.length)];
        const msg = pool.messages[Math.floor(Math.random() * pool.messages.length)];
        
        now.setMinutes(now.getMinutes() - Math.floor(Math.random() * 45) - 3);
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        const hh = String(now.getHours()).padStart(2, '0');
        const min = String(now.getMinutes()).padStart(2, '0');

        let changes = undefined;
        let fileInfo = undefined;

        if (pool.action === '수정') {
            changes = [
                { field: '접근 권한', before: '읽기 전용', after: '읽기/쓰기 (전체 허용)' },
                { field: '계정 활성 상태', before: '승인 대기', after: '활성 (승인 완료)' }
            ];
        } else if (pool.action === '등록') {
            changes = [
                { field: '신규 데이터 추가', before: '-', after: '정상 데이터베이스 편입 (ID: 9921)' }
            ];
        } else if (pool.action === '삭제') {
            changes = [
                { field: '제거 대상 데이터', before: '2023년 전체 운행 기록 (총 15,201건)', after: '완전 삭제 (복구 불가 상태)' }
            ];
        } else if (pool.action === '다운로드') {
            fileInfo = { fileName: 'report_monthly_2024.xlsx', fileSize: '12.4 MB', type: 'Excel Spreadsheet 분석 원본' };
        } else if (pool.action === '이메일 전송') {
            fileInfo = { recipient: 'manager@eview.app 외 11명 (마케팅팀)', subject: '[기밀] 2월 운행 및 배터리 종합 요약본', attached: 'report_summary.pdf (2.1 MB)' };
        }

        logs.push({
            id: `log_${i}`,
            timestamp: `${yyyy}-${mm}-${dd} ${hh}:${min}`,
            userName: user.name,
            userEmail: user.email,
            menuPath: pool.path,
            action: pool.action,
            message: msg,
            ipAddress: user.ip,
            details: { changes, fileInfo }
        });
    }
    return logs;
};

const LogViewer: React.FC = () => {
    const [logs] = useState<SimpleLogEntry[]>(generateMockLogs());
    const [searchTerm, setSearchTerm] = useState('');
    const [filterMenu, setFilterMenu] = useState<string>('ALL');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedLog, setSelectedLog] = useState<SimpleLogEntry | null>(null);
    
    // Using 10 items as requested in the previous step
    const pageSize = 10;

    const filteredLogs = useMemo(() => {
        return logs.filter(log => {
            const matchSearch = log.message.includes(searchTerm) || log.action.includes(searchTerm) || log.userName.includes(searchTerm) || log.userEmail.includes(searchTerm) || log.menuPath.includes(searchTerm);
            const matchMenu = filterMenu === 'ALL' || log.menuPath.startsWith(filterMenu);
            return matchSearch && matchMenu;
        });
    }, [logs, searchTerm, filterMenu]);

    const totalPages = Math.ceil(filteredLogs.length / pageSize);
    const pagedLogs = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredLogs.slice(start, start + pageSize);
    }, [filteredLogs, currentPage]);

    return (
        <div className="admin-log-viewer" style={{ height: '900px', display: 'flex', flexDirection: 'column', padding: '0 24px 24px 24px', background: 'white' }}>
            {/* Header & Toolbar */}
            <div style={{ background: 'white', padding: '24px 0 20px 0', borderBottom: '1px solid #e2e8f0', marginBottom: '20px' }}>
                <div className="table-toolbar" style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div className="filters-group" style={{ display: 'flex', gap: '12px' }}>
                        <div className="search-box-wrapper" style={{ width: '280px', position: 'relative' }}>
                            <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: '12px', top: '10px', color: '#94a3b8' }}><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                            <input 
                                type="text" 
                                placeholder="활동 내역, 사용자, 접속 환경 등 검색" 
                                className="admin-search-input"
                                value={searchTerm}
                                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); setSelectedLog(null); }}
                                style={{ paddingLeft: '38px', width: '100%', height: '40px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                            />
                        </div>

                        <select 
                            className="filter-select log-select" 
                            value={filterMenu} 
                            onChange={(e) => { setFilterMenu(e.target.value); setCurrentPage(1); setSelectedLog(null); }}
                            style={{ height: '40px', borderRadius: '8px', border: '1px solid #cbd5e1', padding: '0 16px', fontSize: '14px', color: '#334155', fontWeight: 500 }}
                        >
                            <option value="ALL">전체 메뉴 위치</option>
                            <option value="관리 >">관리 메뉴 (차량, 운전자)</option>
                            <option value="관제 >">관제 메뉴 (배터리 모니터링)</option>
                            <option value="운영 >">운영 메뉴 (운행 기록, 사고이력)</option>
                            <option value="관리자 설정 >">관리자 설정 (계정, 권한)</option>
                        </select>
                    </div>
                    
                    <div className="actions-group">
                        <button 
                            className="btn-save-global outline" 
                            onClick={() => alert("해당 테이블 내역이 엑셀로 내보내졌습니다.")}
                            style={{ height: '40px', display: 'flex', alignItems: 'center', gap: '8px', padding: '0 16px', borderRadius: '8px', border: '1px solid #0ba096', color: '#0ba096', fontWeight: 600, background: 'transparent', cursor: 'pointer' }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                            엑셀로 내보내기
                        </button>
                    </div>
                </div>
            </div>

            {/* Content Area (Modal Layout) */}
            <div className="data-table-wrapper" style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div style={{ flex: 1, overflowY: 'auto' }}>
                    <table className="data-table" style={{ margin: 0, width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                <th style={{ padding: '16px 24px', fontSize: '14px', fontWeight: 600, color: '#475569', width: '180px' }}>발생 일시</th>
                                <th style={{ padding: '16px 24px', fontSize: '14px', fontWeight: 600, color: '#475569', width: '250px' }}>작업자 (계정)</th>
                                <th style={{ padding: '16px 24px', fontSize: '14px', fontWeight: 600, color: '#475569', width: '320px' }}>메뉴 위치 (대메뉴 &gt; 소메뉴)</th>
                                <th style={{ padding: '16px 24px', fontSize: '14px', fontWeight: 600, color: '#475569', width: '140px', textAlign: 'center' }}>분류</th>
                                <th style={{ padding: '16px 24px', fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'right' }}>접속 허용 IP</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pagedLogs.map(log => {
                                return (
                                    <tr 
                                        key={log.id} 
                                        onClick={() => setSelectedLog(log)}
                                        style={{ 
                                            borderBottom: '1px solid #e2e8f0', 
                                            transition: 'all 0.2s', 
                                            cursor: 'pointer',
                                            background: 'white'
                                        }} 
                                        onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f8fafc'; }} 
                                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'white'; }}
                                    >
                                        <td style={{ padding: '16px 24px', fontSize: '14.5px', color: '#64748b' }}>{log.timestamp}</td>
                                        <td style={{ padding: '16px 24px' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '15px' }}>{log.userName}</span>
                                                <span style={{ fontSize: '13px', color: '#64748b' }}>{log.userEmail}</span>
                                            </div>
                                        </td>
                                        <td style={{ padding: '16px 24px' }}>
                                            <div style={{ display: 'inline-flex', alignItems: 'center', background: '#f8fafc', border: `1px solid #e2e8f0`, borderRadius: '4px', padding: '4px 8px', gap: '6px' }}>
                                                <span style={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>{log.menuPath.split(' > ')[0]}</span>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                                                <span style={{ fontSize: '13.5px', fontWeight: 700, color: '#0f172a' }}>{log.menuPath.split(' > ')[1]}</span>
                                            </div>
                                        </td>
                                        <td style={{ padding: '16px 24px', textAlign: 'center' }}>
                                            <span style={{ 
                                                display: 'inline-block', padding: '6px 14px', borderRadius: '4px', fontSize: '13px', fontWeight: 700,
                                                ...(
                                                    log.action === '삭제' ? { color: '#ef4444', background: '#fef2f2' } :
                                                    log.action === '등록' ? { color: '#0ea5e9', background: '#f0f9ff' } :
                                                    log.action === '수정' ? { color: '#10b981', background: '#ecfdf5' } :
                                                    log.action === '다운로드' ? { color: '#8b5cf6', background: '#f5f3ff' } :
                                                    { color: '#475569', background: '#f1f5f9' } 
                                                )
                                            }}>{log.action}</span>
                                        </td>
                                        <td style={{ padding: '16px 24px', fontSize: '13px', color: '#94a3b8', fontFamily: 'monospace', textAlign: 'right' }}>{log.ipAddress}</td>
                                    </tr>
                                );
                            })}
                            {filteredLogs.length === 0 && (
                                <tr>
                                    <td colSpan={5} style={{ padding: '60px', textAlign: 'center', color: '#64748b', fontSize: '15px' }}>선택한 조건에 일치하는 접속 기록이 존재하지 않습니다.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="admin-pagination" style={{ borderTop: '1px solid #e2e8f0', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white' }}>
                    <div className="pagination-info" style={{ color: '#64748b', fontSize: '14px' }}>
                        전체 <strong style={{color: '#0f172a'}}>{filteredLogs.length}</strong>건 중 <strong style={{color: '#0f172a'}}>{filteredLogs.length === 0 ? 0 : (currentPage-1)*pageSize + 1}-{Math.min(filteredLogs.length, currentPage*pageSize)}</strong> 표시
                    </div>
                    <div className="pagination-controls" style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn-page-arrow" disabled={currentPage === 1} onClick={() => {setCurrentPage(prev => prev - 1); setSelectedLog(null);}}>&lt;</button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).slice(Math.max(0, currentPage - 3), Math.min(totalPages, currentPage + 2)).map(page => (
                            <button key={page} className={`btn-page-number ${currentPage === page ? 'selected' : ''}`} onClick={() => {setCurrentPage(page); setSelectedLog(null);}}>
                                {page}
                            </button>
                        ))}
                        <button className="btn-page-arrow" disabled={currentPage === totalPages || totalPages === 0} onClick={() => {setCurrentPage(prev => prev + 1); setSelectedLog(null);}}>&gt;</button>
                    </div>
                </div>
            </div>

            {/* Modal Overlay Details View */}
            {selectedLog && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, backdropFilter: 'blur(3px)' }} onClick={() => setSelectedLog(null)}>
                    <div style={{ background: 'white', width: '640px', borderRadius: '16px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
                        <div style={{ padding: '24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>로그 상세 내역</h3>
                            <button onClick={() => setSelectedLog(null)} style={{ background: 'none', border: 'none', fontSize: '28px', color: '#94a3b8', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
                        </div>
                        <div style={{ padding: '32px 24px', overflowY: 'auto', flex: 1 }}>
                            <div style={{ marginBottom: '32px' }}>
                                <h4 style={{ fontSize: '14px', color: '#475569', margin: '0 0 12px 0', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>1. 작업 개요 및 결과 메시지</h4>
                                <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'grid', gridTemplateColumns: '120px 1fr', gap: '12px 0', alignItems: 'center' }}>
                                    
                                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#64748b' }}>발생 위치 및 시각</div>
                                    <div style={{ fontSize: '14px', color: '#0f172a', fontWeight: 500 }}>{selectedLog.menuPath} <span style={{ color: '#94a3b8', margin: '0 8px' }}>|</span> {selectedLog.timestamp}</div>
                                    
                                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#64748b' }}>작업자 및 IP</div>
                                    <div style={{ fontSize: '14px', color: '#0f172a', fontWeight: 500 }}>{selectedLog.userName} ({selectedLog.userEmail}) <span style={{ color: '#94a3b8', margin: '0 8px' }}>|</span> IP: {selectedLog.ipAddress}</div>
                                    
                                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#64748b' }}>결과 메시지</div>
                                    <div style={{ padding: '10px 14px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px', color: '#0f172a', fontWeight: 600 }}>{selectedLog.message}</div>
                                </div>
                            </div>

                            {selectedLog.details.changes && (
                                <div style={{ marginBottom: '16px' }}>
                                    <h4 style={{ fontSize: '14px', color: '#475569', margin: '0 0 12px 0', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>2. 데이터 전후 변경 내역 비교</h4>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                                        <thead>
                                            <tr style={{ background: '#f8fafc' }}>
                                                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0', width: '120px' }}>변경 속성 항목</th>
                                                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0', width: '180px' }}>기존 상태</th>
                                                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0', width: 'auto' }}>신규 상태</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedLog.details.changes.map((c: any, i: number) => (
                                                <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#fcfcfc' }}>
                                                    <td style={{ padding: '16px', fontSize: '13.5px', fontWeight: 700, color: '#334155', borderBottom: '1px solid #f1f5f9' }}>{c.field}</td>
                                                    <td style={{ padding: '16px', fontSize: '13.5px', color: c.before !== '-' ? '#ef4444' : '#94a3b8', textDecoration: c.before !== '-' ? 'line-through' : 'none', borderBottom: '1px solid #f1f5f9' }}>{c.before}</td>
                                                    <td style={{ padding: '16px', fontSize: '13.5px', color: '#10b981', fontWeight: 700, borderBottom: '1px solid #f1f5f9' }}>{c.after}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {selectedLog.details.fileInfo && (
                                <div style={{ marginBottom: '16px' }}>
                                    <h4 style={{ fontSize: '14px', color: '#475569', margin: '0 0 12px 0', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>2. 파일 반출 내역 상세</h4>
                                    <div style={{ background: '#f5f3ff', padding: '20px', borderRadius: '12px', border: '1px solid #ede9fe' }}>
                                        {Object.entries(selectedLog.details.fileInfo).map(([k, v]) => (
                                            <div key={k} style={{ display: 'flex', marginBottom: '10px', alignItems: 'center' }}>
                                                <div style={{ width: '120px', fontSize: '13px', fontWeight: 700, color: '#7c3aed', flexShrink: 0 }}>
                                                    {k === 'fileName' ? '출력 파일명' : k === 'fileSize' ? '파일 용량' : k === 'type' ? '확장 포맷' : k === 'recipient' ? '수신자 목록' : k === 'subject' ? '발송 제목' : '첨부 파일'}
                                                </div>
                                                <div style={{ fontSize: '14px', color: '#4c1d95', fontWeight: 500 }}>{v as React.ReactNode}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div style={{ padding: '20px 24px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
                            <button className="btn-save-global" style={{ padding: '0 24px', height: '46px', fontSize: '15px' }} onClick={() => setSelectedLog(null)}>닫기</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LogViewer;
